
import { defineComponent, ref, computed, watch, onMounted } from 'vue'
import { CurrencyInputOptions, CurrencyDisplay } from 'vue-currency-input'
import { debounce } from '@/use/debounce'
import { currencyFormatBR } from '@/use/numberFormatBR'
import { CreditData } from '@/config/variables'
import FormTextInput from '@/components/ui/FormTextInput.vue'
import InfoBox from '@/components/ui/InfoBox.vue'
import ButtonDefault from '@/components/ui/ButtonDefault.vue'
import SliderInput from '@/components/ui/SliderInput.vue'
import MultiSelect from '@/components/ui/MultiSelect.vue'
import InputError from '@/components/ui/InputError.vue'
import { c4bApi } from '@/api/C4bApi'
import Slider from '@vueform/slider'
import ICredit from '@/types/credit'
import { theme } from '@/config/styles'

enum ERequestStatus {
    Idle,
    Debounced,
    InProgress,
    Done,
}

const __default__ = defineComponent({
    props: {
        limit: {
            type: Number,
            required: true,
        },
        installment: {
            type: Number,
            required: true,
        },
    },
    components: {
        InfoBox,
        ButtonDefault,
        SliderInput,
        FormTextInput,
        MultiSelect,
        InputError,
        Slider,
    },
    emits: ['formButtonClicked', 'valuesChanged'],
    setup: (props, context) => {
        const inputStart = ref(false)
        const minCredit = 10000
        const maxCredit = 5000000
        const creditUser = ref({
            limit: props.limit,
            installment: props.installment,
        })
        const currencyOptions: CurrencyInputOptions = {
            currency: 'BRL',
            currencyDisplay: CurrencyDisplay.symbol,
            locale: 'pt-BR',
            hideCurrencySymbolOnFocus: false,
            hideGroupingSeparatorOnFocus: true,
            hideNegligibleDecimalDigitsOnFocus: true,
            autoDecimalDigits: false,
            autoSign: true,
            useGrouping: true,
        }
        const creditData = CreditData
        const lenghReason = creditData.text.listReasons.length
        const reason = ref('')
        const reasonOthers = ref('')
        const others = creditData.text.listReasons[lenghReason - 1]
        const isInvalid = ref(false)
        const income = ref(0)
        const requestStatus = ref(ERequestStatus.Idle)
        const invalidCredit = ref(false)
        const errorsCredit = [
            'O crédito precisa ser maior que R$10.000,00' +
                ' e menor que R$5.000.000,00',
        ]
        /**
         * Está função serve para formatar o número mostrado no
         * tooltip do slider
         * @param {number} value - Numbero recebido do slider
         */
        const format = (value: number): string => {
            return `R$ ${currencyFormatBR(value)}`
        }
        /**
         * Função utilizada para limpar o campo do motivo no form
         */
        const reset = (): void => {
            reasonOthers.value = ''
            isInvalid.value = false
            creditUser.value.limit = 10000
            creditUser.value.installment = 6
        }
        /** Função para validar o multiselect */
        const handleReasonSelect = (reason: string): void => {
            if (reason == '' || reason == undefined) isInvalid.value = true
            else isInvalid.value = false
        }
        /** Função utilizada para validar o input de outro motivos */
        const validationReasonOthers = (): void => {
            handleReasonSelect(reason.value)
            if (reason.value == others)
                if (reasonOthers.value == '' || reasonOthers.value.length < 10)
                    isInvalid.value = true
                else isInvalid.value = false
        }
        /**
         * Função utilizada para converter um numero em formato real
         * para sem formato.
         * @param {string} value - String do valor formatado
         * @return {number} - retorna o valor formatado sem formato.
         */
        const convertToNumber = (value: string): number => {
            const newValue = value
                .replace(/,/g, '')
                .replace(/\./g, '')
                .replace(/R/g, '')
                .replace(/\$/g, '')
            return Number(newValue.substr(0, newValue.length - 2))
        }
        /**
         * Função utilizada para pegar o valor do input e colocar no slider
         * com um debounce de 500ms
         * @param {string} inputSlide - O input do slider.
         */
        const changeSlide = debounce((inputSlide: string) => {
            const newvalue = convertToNumber(inputSlide)
            creditUser.value.limit = newvalue
        }, 500)

        /**
         * variavel computed com posibilidade de pegar o valor atual
         * e setar um novo com debounce.
         */
        const creditFormatted = computed({
            get: () =>
                'R$ ' +
                currencyFormatBR(
                    inputStart.value == true ? creditUser.value.limit : 0
                ),
            set: (value) => {
                changeSlide(value)
            },
        })
        const minIncome = computed(() => {
            return currencyFormatBR(income.value)
        })
        const handleSubmit = () => {
            context.emit(
                'valuesChanged',
                creditUser.value.limit,
                creditUser.value.installment
            )
            validationReasonOthers()
            if (invalidCredit.value == true) return
            if (isInvalid.value == true) return
            else if (reason.value === others)
                context.emit('formButtonClicked', reasonOthers.value, reset)
            else context.emit('formButtonClicked', reason.value, reset)
        }
        const CalculateIncome = async (creditUser: ICredit) => {
            try {
                const recomendedIncome = await c4bApi.credit().post(creditUser)
                return recomendedIncome.data
            } catch (error: any) {
                console.log(error)
            }
        }
        /** Calculo para os valores por defeto do credito */
        onMounted(() => {
            requestStatus.value = ERequestStatus.InProgress
            calMinIncome(creditUser.value)
        })
        /** Este evento é acionado sempre que o multiselect é clicado.*/
        watch(reason, (reason) => handleReasonSelect(reason))
        /** Espera que o slider do limite de crédito mude de valor */
        watch(
            creditUser,
            (currentCreditUser) => {
                requestStatus.value = ERequestStatus.Debounced

                if (
                    (currentCreditUser.limit < 10000 ||
                        currentCreditUser.limit > 5000000) &&
                    inputStart.value == true
                )
                    invalidCredit.value = true
                else invalidCredit.value = false
                inputStart.value = true
                calMinIncomeDebounce(currentCreditUser)
            },
            {
                deep: true,
            }
        )
        /** Função asíncrona que espera que o backend faça o cálculo do
         * faturamento recomendado */
        const calMinIncome = async (creditUser: ICredit) => {
            const minIncome = await CalculateIncome(creditUser)
            if (creditUser.limit <= 0 || creditUser.installment <= 0) return 0
            if (requestStatus.value != ERequestStatus.Debounced) {
                income.value = minIncome
                requestStatus.value = ERequestStatus.Done
            }
        }
        /** Wrapper de funcao calMinIncome para adicionar um delay */
        const calMinIncomeDebounce = debounce(
            (creditUser: ICredit) => {
                requestStatus.value = ERequestStatus.InProgress
                calMinIncome(creditUser)
            },
            500 // 500ms
        )
        return {
            handleSubmit,
            currencyFormatBR,
            minIncome,
            creditUser,
            creditFormatted,
            currencyOptions,
            minCredit,
            maxCredit,
            creditData,
            reason,
            others,
            reasonOthers,
            isInvalid,
            requestStatus,
            ERequestStatus,
            format,
            errorsCredit,
            invalidCredit,
            theme
        }
    },
})

import { useCssVars as _useCssVars } from 'vue'
const __injectCSSVars__ = () => {
_useCssVars(_ctx => ({
  "55200372": (_ctx.theme.colors.secondary.light),
  "7c324dcc": (_ctx.theme.colors.secondary.middark),
  "31a5fad5": (_ctx.theme.colors.black)
}))}
const __setup__ = __default__.setup
__default__.setup = __setup__
  ? (props, ctx) => { __injectCSSVars__();return __setup__(props, ctx) }
  : __injectCSSVars__

export default __default__